// src/lib/travelerData.ts
import { supabase } from '../supabaseClient';
import { fetchVouchersForOrders } from './vouchers';

export type TravelerSession = {
  user: any;
  uid: string;
  email: string;
};

export type TravelerCounts = {
  alerts: number;
  reservations: number;
  favorites: number;
  orders: number;
};

const trim = (value: unknown) => String(value || '').trim();

export const moneyCad = (value?: number | string | null, options?: { cents?: boolean }) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'À confirmer';
  const amount = options?.cents ? n / 100 : n > 1000 ? n / 100 : n;
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(amount);
};

export const dateTime = (value?: string | null) => {
  if (!value) return 'Date à confirmer';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Date à confirmer';
  return d.toLocaleString('fr-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const dateOnly = (value?: string | null) => {
  if (!value) return 'Date inconnue';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Date inconnue';
  return d.toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const displayStatus = (value?: string | null) => {
  const v = trim(value || 'confirmed').toLowerCase();
  const map: Record<string, string> = {
    paid: 'Payé',
    confirmed: 'Confirmé',
    pending: 'En attente',
    pending_payment: 'Paiement en attente',
    shipped: 'Envoyé',
    active: 'Actif',
    cancelled: 'Annulé',
    canceled: 'Annulé',
    failed: 'Échec',
  };
  return map[v] || value || 'Confirmé';
};

export const getTravelerSession = async (): Promise<TravelerSession | null> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session;
  if (!session?.user) return null;
  return {
    user: session.user,
    uid: session.user.id,
    email: session.user.email || '',
  };
};

const safeCount = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const fetchEvasionAlerts = async (uid: string, limit?: number) => {
  let query = supabase
    .from('evasion_alerts')
    .select('*', { count: 'exact' })
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error, count } = await query;
  if (error) throw error;
  return { data: data || [], count: safeCount(count) };
};

export const fetchReservations = async (uid: string, email: string, limit?: number) => {
  const baseSelect = '*, business:businesses(id,name,address,region,city,phone,website,link,image_url,logo_url)';
  const emailTrimmed = trim(email).toLowerCase();
  const clauses = [`user_id.eq.${uid}`];
  if (emailTrimmed) clauses.push(`customer_email.eq.${emailTrimmed}`);

  let query = supabase
    .from('reservations')
    .select(baseSelect, { count: 'exact' })
    .or(clauses.join(','))
    .order('created_at', { ascending: false });
  if (limit) query = query.limit(limit);

  const { data, error, count } = await query;
  if (!error) return { data: data || [], count: safeCount(count) };

  // Fallback si la relation business:businesses n'est pas disponible dans une ancienne base.
  let fallback = supabase
    .from('reservations')
    .select('*', { count: 'exact' })
    .or(clauses.join(','))
    .order('created_at', { ascending: false });
  if (limit) fallback = fallback.limit(limit);
  const fallbackRes = await fallback;
  if (fallbackRes.error) throw fallbackRes.error;
  return { data: fallbackRes.data || [], count: safeCount(fallbackRes.count) };
};

const fetchOrderItemsForOrders = async (orderIds: string[]) => {
  const ids = orderIds.filter(Boolean);
  if (ids.length === 0) return new Map<string, any[]>();

  try {
    const { data, error } = await supabase
      .from('shop_order_items')
      .select('*')
      .in('order_id', ids)
      .order('created_at', { ascending: true });

    if (error || !Array.isArray(data)) {
      if (error) console.warn('shop_order_items unavailable:', error.message);
      return new Map<string, any[]>();
    }

    const map = new Map<string, any[]>();
    data.forEach((item: any) => {
      const key = String(item.order_id || '');
      if (!key) return;
      const list = map.get(key) || [];
      list.push(item);
      map.set(key, list);
    });

    return map;
  } catch (err) {
    console.warn('fetchOrderItemsForOrders failed:', err);
    return new Map<string, any[]>();
  }
};

export const fetchOrders = async (uid: string, email: string, limit?: number) => {
  const emailTrimmed = trim(email).toLowerCase();
  const clauses = [`user_id.eq.${uid}`];
  if (emailTrimmed) clauses.push(`customer_email.eq.${emailTrimmed}`);

  let query = supabase
    .from('shop_orders')
    .select('*', { count: 'exact' })
    .or(clauses.join(','))
    .order('created_at', { ascending: false });
  if (limit) query = query.limit(limit);

  const { data, error, count } = await query;
  if (error) throw error;

  const orders = data || [];
  const orderIds = orders.map((order: any) => String(order.id || '')).filter(Boolean);
  const itemMap = await fetchOrderItemsForOrders(orderIds);
  const voucherMap = await fetchVouchersForOrders(orderIds);

  return {
    data: orders.map((order: any) => ({
      ...order,
      items: itemMap.get(String(order.id || '')) || order.items || [],
      vouchers: voucherMap.get(String(order.id || '')) || order.vouchers || [],
    })),
    count: safeCount(count),
  };
};

export const getOrderItems = (order: any) => {
  const directItems = Array.isArray(order?.items) ? order.items : [];
  if (directItems.length > 0) return directItems;

  const metadataItems = order?.metadata?.cart_items;
  if (Array.isArray(metadataItems)) return metadataItems;

  const payloadItems = order?.metadata?.custom_payload;
  if (Array.isArray(payloadItems)) return payloadItems;

  return [];
};

export const getOrderVouchers = (order: any) => {
  return Array.isArray(order?.vouchers) ? order.vouchers : [];
};

export const getOrderSubtotal = (order: any) => {
  return order?.subtotal_cents ?? order?.metadata?.totals?.subtotal_cents ?? null;
};

export const getOrderTps = (order: any) => {
  return order?.tps_cents ?? order?.metadata?.totals?.tps_cents ?? null;
};

export const getOrderTvq = (order: any) => {
  return order?.tvq_cents ?? order?.metadata?.totals?.tvq_cents ?? null;
};

export const getOrderTitle = (item: any) => {
  return (
    item?.title ||
    item?.product_title ||
    item?.name ||
    item?.metadata?.title ||
    item?.metadata?.name ||
    item?.metadata?.product_title ||
    item?.p ||
    'Achat Forfeo'
  );
};

export const getOrderItemTotal = (item: any) => {
  return (
    item?.total_price_cents ??
    item?.amount_cents ??
    item?.unit_price_cents ??
    item?.price_cents ??
    item?.metadata?.total_price_cents ??
    item?.metadata?.price_cents ??
    null
  );
};

export const fetchFavorites = async (uid: string, limit?: number) => {
  let query = supabase
    .from('favorites')
    .select('*', { count: 'exact' })
    .eq('user_id', uid)
    .order('created_at', { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error, count } = await query;
  if (error) throw error;
  return { data: data || [], count: safeCount(count) };
};

export const getOrderTotal = (order: any) => {
  return order?.total_cents ?? order?.amount_total ?? order?.total ?? order?.amount ?? order?.metadata?.stripe_amount_total ?? null;
};

export const getReservationPrice = (reservation: any) => {
  return reservation?.total_price ?? reservation?.amount ?? reservation?.price ?? reservation?.amount_cents ?? null;
};
